import { X } from "lucide-react";

function AddInterventionPointsInfo({ setShowValidationGuide }) {
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setShowValidationGuide(false);
        }
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            📋 Data Validation & Duplicate Prevention Guide
          </h2>
          <button
            onClick={() => setShowValidationGuide(false)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Data Validation Requirements */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
              📋 Data Validation Requirements
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                <h4 className="font-semibold text-red-800 dark:text-red-200 mb-2">
                  Required Fields
                </h4>
                <ul className="space-y-1 text-sm text-red-700 dark:text-red-300">
                  <li>
                    ✅ <strong>Latitude</strong> (decimal number)
                  </li>
                  <li>
                    ✅ <strong>Longitude</strong> (decimal number)
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
                  Optional Fields
                </h4>
                <ul className="space-y-1 text-sm text-yellow-700 dark:text-yellow-300">
                  <li>🟡 Name (text)</li>
                  <li>🟡 Shop Sign ID (integer)</li>
                  <li>🟡 Address, City, Zip Code, Phone</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Field Validation Rules */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
              🎯 Field-by-Field Validation Rules
            </h3>

            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
                  1. Latitude (REQUIRED)
                </h4>
                <div className="bg-gray-800 dark:bg-gray-900 text-gray-100 p-3 rounded font-mono text-sm">
                  <div>Valid Range: -90 to +90</div>
                  <div>Format: Decimal number</div>
                  <div>
                    Examples: ✅ 48.8566 ✅ -23.5505 ❌ 95.123 ❌ "North"
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
                  2. Longitude (REQUIRED)
                </h4>
                <div className="bg-gray-800 dark:bg-gray-900 text-gray-100 p-3 rounded font-mono text-sm">
                  <div>Valid Range: -180 to +180</div>
                  <div>Format: Decimal number</div>
                  <div>
                    Examples: ✅ 2.3522 ✅ -46.6333 ❌ 185.456 ❌ "East"
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
                  3. Shop Sign ID (Optional)
                </h4>
                <div className="bg-gray-800 dark:bg-gray-900 text-gray-100 p-3 rounded font-mono text-sm">
                  <div>Format: Whole number (integer)</div>
                  <div>Must exist in the system's shop signs database</div>
                  <div>Examples: ✅ 123 ✅ 4567 ❌ 123.45 ❌ "ABC"</div>
                </div>
              </div>
            </div>
          </div>

          {/* Duplicate Prevention */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
              🔍 How Duplicate Prevention Works
            </h3>

            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800 mb-4">
              <p className="text-blue-800 dark:text-blue-200 text-sm mb-3">
                The system uses <strong>Google Plus Codes</strong> (location
                identifiers) to prevent duplicates at two levels:
              </p>

              <div className="space-y-4">
                <div className="bg-white dark:bg-gray-800 p-3 rounded border">
                  <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
                    Level 1: Exact Location Duplicate Prevention
                  </h4>
                  <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <li>
                      <strong>What it checks:</strong> Identical coordinates
                      (same Plus Code)
                    </li>
                    <li>
                      <strong>Precision:</strong> ~10 meter accuracy
                    </li>
                    <li>
                      <strong>Action:</strong> Skips the new record, keeps
                      existing one
                    </li>
                    <li>
                      <strong>Message:</strong> "Intervention point with
                      PLUS_CODE already exists"
                    </li>
                  </ul>
                </div>

                <div className="bg-white dark:bg-gray-800 p-3 rounded border">
                  <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
                    Level 2: Area + Shop Sign Duplicate Prevention
                  </h4>
                  <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <li>
                      <strong>What it checks:</strong> Same shop sign within
                      ~14km area
                    </li>
                    <li>
                      <strong>Precision:</strong> 8-character Plus Code area
                    </li>
                    <li>
                      <strong>Action:</strong> Skips if same shop_sign_id exists
                      in the area
                    </li>
                    <li>
                      <strong>Message:</strong> "Intervention point with
                      SHOP_SIGN_ID and WIDER_PLUS_CODE already exists"
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddInterventionPointsInfo;
